import React from "react";
import {
    DialogOverlay,
    DialogBox,
    DialogHeader,
    DialogBody,
    DialogFooter,
    DialogCloseBtn,
    DialogBtn,
    FormField,
    InfoText,
} from "./ANCInvestigationStyles";

/**
 * Investigation type options
 */
const INVESTIGATION_OPTIONS = [
    { value: "hiv", label: "HIV Test" },
    { value: "syphilis", label: "Syphilis (VDRL/RPR)" },
    { value: "hepatitis_b", label: "Hepatitis B (HBsAg)" },
    { value: "blood_group", label: "Blood Group & Rh" },
    { value: "haemoglobin", label: "Haemoglobin (Hb)" },
    { value: "blood_sugar", label: "Blood Sugar (RBS/FBS)" },
    { value: "urinalysis", label: "Urinalysis" },
    { value: "malaria", label: "Malaria (mRDT/BS)" },
    { value: "sickling", label: "Sickling Test" },
    { value: "ultrasound", label: "Obstetric Ultrasound" },
    { value: "viral_load", label: "Viral Load (HIV+)" },
    { value: "cd4", label: "CD4 Count (HIV+)" },
];

/**
 * Result status options
 */
const RESULT_STATUS_OPTIONS = [
    { value: "normal", label: "Normal" },
    { value: "abnormal", label: "Abnormal" },
    { value: "critical", label: "Critical" },
];

/**
 * Specimen type mapping
 */
const SPECIMEN_MAP = {
    hiv: "Blood",
    syphilis: "Blood",
    hepatitis_b: "Blood",
    blood_group: "Blood",
    haemoglobin: "Blood",
    blood_sugar: "Blood",
    urinalysis: "Urine",
    malaria: "Blood",
    sickling: "Blood",
    ultrasound: "N/A (Imaging)",
    viral_load: "Blood",
    cd4: "Blood",
};

/**
 * Result hint per investigation type
 */
const RESULT_HINTS = {
    hiv: "e.g., Reactive, Non-reactive",
    syphilis: "e.g., Reactive, Non-reactive",
    hepatitis_b: "e.g., Reactive, Non-reactive",
    blood_group: "e.g., A+, B-, O+, AB+",
    haemoglobin: "e.g., 11.5 g/dL",
    blood_sugar: "e.g., 5.6 mmol/L",
    urinalysis: "e.g., Protein: nil, Glucose: trace",
    malaria: "e.g., Positive, Negative",
    sickling: "e.g., Negative, AS, SS",
    ultrasound: "e.g., Single viable fetus, EGA consistent",
    viral_load: "e.g., <50 copies/mL",
    cd4: "e.g., 450 cells/μL",
};

function getInvestLabel(key) {
    const found = INVESTIGATION_OPTIONS.find(function(o) { return o.value === key; });
    return found ? found.label : key;
}

/**
 * ANCOrderDialog — Dialog for ordering a new investigation
 */
export const ANCOrderDialog = function(props) {
    const open = props.open;
    const investigationType = props.investigationType;
    const formData = props.formData;
    const onFormChange = props.onFormChange;
    const onSubmit = props.onSubmit;
    const onClose = props.onClose;
    const saving = props.saving;

    if (!open) return null;

    return (
        <DialogOverlay onClick={function(e) { if (e.target === e.currentTarget) onClose(); }}>
            <DialogBox>
                <DialogHeader>
                    <h4>🧪 Order Investigation</h4>
                    <DialogCloseBtn onClick={onClose}>✕</DialogCloseBtn>
                </DialogHeader>

                <DialogBody>
                    <FormField>
                        <label>Investigation Type</label>
                        {investigationType ? (
                            <InfoText>
                                {getInvestLabel(investigationType)}
                                <div style={{ fontSize: "0.75rem", color: "#78909c", marginTop: "4px" }}>
                                    Specimen: {SPECIMEN_MAP[investigationType] || "N/A"}
                                </div>
                            </InfoText>
                        ) : (
                            <select
                                value={formData.investigation_type}
                                onChange={function(e) { onFormChange("investigation_type", e.target.value); }}
                            >
                                <option value="">— Select Investigation —</option>
                                {INVESTIGATION_OPTIONS.map(function(opt) {
                                    return <option key={opt.value} value={opt.value}>{opt.label}</option>;
                                })}
                            </select>
                        )}
                    </FormField>

                    <FormField>
                        <label>Date Ordered</label>
                        <input
                            type="date"
                            value={formData.ordered_date || ""}
                            onChange={function(e) { onFormChange("ordered_date", e.target.value); }}
                        />
                    </FormField>

                    <FormField>
                        <label>Notes (optional)</label>
                        <textarea
                            value={formData.notes || ""}
                            onChange={function(e) { onFormChange("notes", e.target.value); }}
                            placeholder="e.g., Urgent, Repeat test, Fasting sample..."
                            rows={3}
                        />
                    </FormField>
                </DialogBody>

                <DialogFooter>
                    <DialogBtn onClick={onClose}>Cancel</DialogBtn>
                    <DialogBtn
                        variant="primary"
                        onClick={onSubmit}
                        disabled={saving || !(formData.investigation_type || investigationType)}
                    >
                        {saving ? "Ordering..." : "Order Investigation"}
                    </DialogBtn>
                </DialogFooter>
            </DialogBox>
        </DialogOverlay>
    );
};

/**
 * ANCResultDialog — Dialog for entering/updating investigation results
 */
export const ANCResultDialog = function(props) {
    const open = props.open;
    const investigation = props.investigation;
    const formData = props.formData;
    const onFormChange = props.onFormChange;
    const onSubmit = props.onSubmit;
    const onClose = props.onClose;
    const saving = props.saving;

    if (!open || !investigation) return null;

    const invType = investigation.investigation_type || investigation.key || formData.investigation_type || "";
    const hint = RESULT_HINTS[invType] || "";

    return (
        <DialogOverlay onClick={function(e) { if (e.target === e.currentTarget) onClose(); }}>
            <DialogBox>
                <DialogHeader>
                    <h4>📝 {formData.result_value ? "Update" : "Enter"} Result</h4>
                    <DialogCloseBtn onClick={onClose}>✕</DialogCloseBtn>
                </DialogHeader>

                <DialogBody>
                    <FormField>
                        <label>Investigation</label>
                        <InfoText>
                            {getInvestLabel(invType)}
                            {investigation.ordered_date && (
                                <div style={{ fontSize: "0.75rem", color: "#78909c", marginTop: "4px" }}>
                                    Ordered: {investigation.ordered_date}
                                </div>
                            )}
                        </InfoText>
                    </FormField>

                    <FormField>
                        <label>Result Date</label>
                        <input
                            type="date"
                            value={formData.result_date || ""}
                            onChange={function(e) { onFormChange("result_date", e.target.value); }}
                        />
                    </FormField>

                    <FormField>
                        <label>Result Value</label>
                        <input
                            type="text"
                            value={formData.result_value || ""}
                            onChange={function(e) { onFormChange("result_value", e.target.value); }}
                            placeholder={hint || "Enter result..."}
                        />
                        {hint && (
                            <div style={{ fontSize: "0.72rem", color: "#90a4ae", marginTop: "4px" }}>
                                💡 {hint}
                            </div>
                        )}
                    </FormField>

                    <FormField>
                        <label>Result Status</label>
                        <select
                            value={formData.result_status || ""}
                            onChange={function(e) { onFormChange("result_status", e.target.value); }}
                        >
                            <option value="">— Auto-detect —</option>
                            {RESULT_STATUS_OPTIONS.map(function(opt) {
                                return <option key={opt.value} value={opt.value}>{opt.label}</option>;
                            })}
                        </select>
                    </FormField>

                    <FormField>
                        <label>Notes (optional)</label>
                        <textarea
                            value={formData.notes || ""}
                            onChange={function(e) { onFormChange("notes", e.target.value); }}
                            placeholder="Additional observations..."
                            rows={3}
                        />
                    </FormField>
                </DialogBody>

                <DialogFooter>
                    <DialogBtn onClick={onClose}>Cancel</DialogBtn>
                    <DialogBtn
                        variant="primary"
                        onClick={onSubmit}
                        disabled={saving || !formData.result_value || !formData.result_date}
                    >
                        {saving ? "Saving..." : "Save Result"}
                    </DialogBtn>
                </DialogFooter>
            </DialogBox>
        </DialogOverlay>
    );
};
